import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePlus, FolderPlus, LayoutDashboard, ListPlus, Play, SquarePlus, Users, Zap } from 'lucide-react';
import { useSession } from '../lib/session';
import { Menu } from '@vatman/ui';
import { openCreate, useHere, useNewBoard } from './spaces/dialogs';
import { useNewPage } from '../docs/model';

/**
 * "Create" for everything (docs/navigation-plan.md): a board, an issue, a sprint, a project, a
 * space, a team. New things go where the person is (`useHere`); the dialog asks what is missing.
 */
export function CreateMenu({ children, align = 'start' }: { children: ReactNode; align?: 'start' | 'end' }) {
  const { t } = useTranslation();
  const session = useSession();
  const here = useHere();
  const newBoard = useNewBoard(session.data!);
  const newPage = useNewPage();
  // A page goes into the space in view, else the first team of the organization in view.
  const team = session.data?.teams.find((tm) => (here.org ? tm.orgId === here.org : tm.orgId === null)) ?? session.data?.teams[0];
  const where = { org: here.org, team: here.team, space: here.space, project: here.project };
  return (
    <Menu.Root>
      <Menu.Trigger asChild>{children}</Menu.Trigger>
      <Menu.Content align={align}>
        <Menu.Item icon={<LayoutDashboard size={16} />} onSelect={() => void newBoard(where)}>
          {t('create.board')}
        </Menu.Item>
        <Menu.Item icon={<FilePlus size={16} />} onSelect={() => void newPage(here.space ? { spaceId: here.space } : { teamId: here.team ?? team?.id })}>
          {t('create.page')}
        </Menu.Item>
        <Menu.Item icon={<SquarePlus size={16} />} onSelect={() => openCreate('issue', where)} keys={here.project ? ['C'] : undefined}>
          {t('create.issue')}
        </Menu.Item>
        <Menu.Item icon={<Zap size={16} />} onSelect={() => openCreate('sprint', where)}>
          {t('create.sprint')}
        </Menu.Item>
        <Menu.Item icon={<Play size={16} />} onSelect={() => openCreate('startSprint', where)}>
          {t('create.startSprint')}
        </Menu.Item>
        <Menu.Separator />
        <Menu.Item icon={<ListPlus size={16} />} onSelect={() => openCreate('project', where)}>
          {t('create.project')}
        </Menu.Item>
        <Menu.Item icon={<FolderPlus size={16} />} onSelect={() => openCreate('space', where)}>
          {t('create.space')}
        </Menu.Item>
        {here.org && (
          <Menu.Item icon={<Users size={16} />} onSelect={() => openCreate('team', { org: here.org })}>
            {t('dashboard.newTeam')}
          </Menu.Item>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}
